// Finance queries (M5-04 over M5-03's views and M3-04's price lists and FX rates). Keys sit under
// the company's (`["company", co, "finance", …]`), so change pings refresh them with everything
// else of the company, and one invalidation of `financeKeys.all(co)` after a write refreshes every
// view that may have moved (a compensation row changes budget vs actual, margins and forecasts).
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { PAGE } from "../queries";

export type PeriodType = "month" | "quarter" | "year";
export type MarginBy = "seat" | "team" | "client";

export const financeKeys = {
  all: (co: string) => ["company", co, "finance"] as const,
  access: (co: string) => ["company", co, "finance", "access"] as const,
  bva: (co: string, type: PeriodType, key: string) =>
    ["company", co, "finance", "budget-vs-actual", type, key] as const,
  margins: (co: string, key: string, by: MarginBy) =>
    ["company", co, "finance", "margins", key, by] as const,
  agentSpend: (co: string, month: string) =>
    ["company", co, "finance", "agent-spend", month] as const,
  forecast: (co: string, month: string) => ["company", co, "finance", "forecast", month] as const,
  scenarios: (co: string) => ["company", co, "finance", "scenarios"] as const,
  scenario: (co: string, id: string) =>
    ["company", co, "finance", "scenarios", "detail", id] as const,
  plan: (co: string) => ["company", co, "finance", "headcount-plan"] as const,
  diff: (co: string, month: string) => ["company", co, "finance", "headcount-diff", month] as const,
  comp: (co: string) => ["company", co, "finance", "compensation"] as const,
  recurring: (co: string) => ["company", co, "finance", "recurring-expenses"] as const,
  clients: (co: string) => ["company", co, "finance", "clients"] as const,
  categories: (co: string) => ["company", co, "finance", "categories"] as const,
  revenue: (co: string, month: string) => ["company", co, "finance", "revenue", month] as const,
  prices: (co: string, unit: string) => ["company", co, "finance", "prices", unit] as const,
  fx: (co: string) => ["company", co, "finance", "fx"] as const,
};

const next = (last: { next_cursor: string | null }) => last.next_cursor ?? undefined;

/** A cursor-paged list ("Load more"; `PAGE.setup` rows a page). */
const paged = <T extends { next_cursor: string | null }>(
  queryKey: readonly unknown[],
  fetch: (query: { limit: number; cursor?: string }) => Promise<T>,
) =>
  infiniteQueryOptions({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      fetch({ limit: PAGE.setup, ...(pageParam ? { cursor: pageParam } : {}) }),
    getNextPageParam: next,
  });

/** Who holds the finance relations, and what the caller holds (every finance page reads it). */
export const financeAccessQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: financeKeys.access(co),
    queryFn: () => api.client.financeAccess.list({ params: { co } }),
  });

export const budgetVsActualQuery = (api: Api, co: string, type: PeriodType, key: string) =>
  queryOptions({
    queryKey: financeKeys.bva(co, type, key),
    queryFn: () =>
      api.client.finance.budgetVsActual({
        params: { co },
        query: { period_type: type, period_key: key },
      }),
  });

export const marginsQuery = (api: Api, co: string, key: string, by: MarginBy) =>
  queryOptions({
    queryKey: financeKeys.margins(co, key, by),
    queryFn: () => api.client.finance.margins({ params: { co }, query: { period_key: key, by } }),
  });

export const agentSpendQuery = (api: Api, co: string, month: string) =>
  queryOptions({
    queryKey: financeKeys.agentSpend(co, month),
    queryFn: () => api.client.finance.agentSpend({ params: { co }, query: { period_key: month } }),
  });

export const forecastQuery = (api: Api, co: string, month: string) =>
  queryOptions({
    queryKey: financeKeys.forecast(co, month),
    queryFn: () => api.client.forecasts.list({ params: { co }, query: { period_key: month } }),
  });

export const scenariosQuery = (api: Api, co: string) =>
  paged(financeKeys.scenarios(co), (query) => api.client.scenarios.list({ params: { co }, query }));

export const headcountPlanQuery = (api: Api, co: string) =>
  paged(financeKeys.plan(co), (query) => api.client.headcountPlan.list({ params: { co }, query }));

export const headcountDiffQuery = (api: Api, co: string, month: string) =>
  queryOptions({
    queryKey: financeKeys.diff(co, month),
    queryFn: () => api.client.headcountPlan.diff({ params: { co }, query: { month } }),
  });

export const compensationQuery = (api: Api, co: string) =>
  paged(financeKeys.comp(co), (query) => api.client.compensation.list({ params: { co }, query }));

export const recurringQuery = (api: Api, co: string) =>
  paged(financeKeys.recurring(co), (query) =>
    api.client.recurringExpenses.list({ params: { co }, query }),
  );

export const clientsQuery = (api: Api, co: string) =>
  paged(financeKeys.clients(co), (query) => api.client.clients.list({ params: { co }, query }));

export const categoriesQuery = (api: Api, co: string) =>
  paged(financeKeys.categories(co), (query) =>
    api.client.categories.list({ params: { co }, query }),
  );

export const revenueQuery = (api: Api, co: string, month: string) =>
  paged(financeKeys.revenue(co, month), (query) =>
    api.client.revenueEvents.list({ params: { co }, query: { ...query, period_key: month } }),
  );

export const pricesQuery = (api: Api, co: string, unit: string) =>
  paged(financeKeys.prices(co, unit), (query) =>
    api.client.priceLists.list({
      params: { co },
      query: { ...query, ...(unit ? { unit: unit as never } : {}) },
    }),
  );

export const fxQuery = (api: Api, co: string) =>
  paged(financeKeys.fx(co), (query) => api.client.fxRates.list({ params: { co }, query }));
